import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import { Building, Save, Upload, X, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const getToken = () => localStorage.getItem('token');

const api = async (endpoint, options = {}) => {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...options.headers
  };
  const response = await fetch(`${API_URL}${endpoint}`, { ...options, headers });
  if (!response.ok) throw new Error(await response.text());
  return response.json();
};

export function CompanySettingsTab() {
  const [companySettings, setCompanySettings] = useState({
    company_name: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    representative_name: '',
    representative_title: '',
    logo: ''
  });
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadCompanySettings();
  }, []);

  const loadCompanySettings = async () => {
    try {
      const data = await api('/api/settings/company');
      setCompanySettings(data);
    } catch (error) {
      console.error('Failed to load company settings:', error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await api('/api/settings/company', {
        method: 'PUT',
        body: JSON.stringify(companySettings)
      });
      toast.success('Company settings saved');
    } catch (error) {
      toast.error('Failed to save company settings');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (event, variant = 'light') => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be smaller than 2MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = getToken();
      const response = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) throw new Error('Upload failed');

      const fileData = await response.json();
      const logoUrl = `${API_URL}/api/files/${fileData.id}?auth=${token}`;
      
      const logoField = variant === 'dark' ? 'logo_dark' : 'logo';
      
      setCompanySettings({
        ...companySettings,
        [logoField]: logoUrl
      });
      
      toast.success(`${variant === 'dark' ? 'Dark' : 'Light'} logo uploaded successfully`);
    } catch (error) {
      toast.error('Failed to upload logo');
      console.error(error);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = (variant = 'light') => {
    const logoField = variant === 'dark' ? 'logo_dark' : 'logo';
    setCompanySettings({
      ...companySettings,
      [logoField]: ''
    });
  };

  const handleChange = (field, value) => {
    setCompanySettings({
      ...companySettings,
      [field]: value
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-[Space_Grotesk] font-semibold tracking-tight flex items-center gap-2">
          <Building className="h-6 w-6" />
          Company Information
        </h2>
        <p className="text-muted-foreground">Manage your company branding and contact details</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Logo Section */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Company Logos</CardTitle>
            <CardDescription>Upload logo variants for light and dark backgrounds (max 2MB, PNG/JPG/SVG)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Light Logo */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Light Background Logo</Label>
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0">
                    {companySettings.logo ? (
                      <div className="relative">
                        <img
                          src={companySettings.logo}
                          alt="Company Logo (Light)"
                          className="h-24 w-24 object-contain border-2 border-gray-200 rounded-lg p-2 bg-white"
                        />
                        <Button
                          variant="destructive"
                          size="sm"
                          className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full"
                          onClick={() => handleRemoveLogo('light')}
                          data-testid="remove-logo-button"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ) : (
                      <div className="h-24 w-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-white">
                        <ImageIcon className="h-8 w-8 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleLogoUpload(e, 'light')}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingLogo}
                      data-testid="upload-logo-button"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {uploadingLogo ? 'Uploading...' : 'Upload'}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Dark Logo */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Dark Background Logo (Optional)</Label>
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0">
                    {companySettings.logo_dark ? (
                      <div className="relative">
                        <img
                          src={companySettings.logo_dark}
                          alt="Company Logo (Dark)"
                          className="h-24 w-24 object-contain border-2 border-gray-700 rounded-lg p-2 bg-gray-900"
                        />
                        <Button
                          variant="destructive"
                          size="sm"
                          className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full"
                          onClick={() => handleRemoveLogo('dark')}
                          data-testid="remove-logo-dark-button"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ) : (
                      <div className="h-24 w-24 border-2 border-dashed border-gray-600 rounded-lg flex items-center justify-center bg-gray-900">
                        <ImageIcon className="h-8 w-8 text-gray-500" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      id="dark-logo-upload"
                      onChange={(e) => handleLogoUpload(e, 'dark')}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => document.getElementById('dark-logo-upload')?.click()}
                      disabled={uploadingLogo}
                      data-testid="upload-logo-dark-button"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {uploadingLogo ? 'Uploading...' : 'Upload'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              💡 Recommended: 400x100px transparent PNG. Dark variant is used for dark-themed emails and documents.
            </p>
          </CardContent>
        </Card>

        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
            <CardDescription>Company name and identity</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="company_name">Company Name *</Label>
              <Input
                id="company_name"
                value={companySettings.company_name}
                onChange={(e) => handleChange('company_name', e.target.value)}
                placeholder="Tidify Cleaning Services"
                data-testid="company-name-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card>
          <CardHeader>
            <CardTitle>Contact Information</CardTitle>
            <CardDescription>How clients can reach you</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                type="tel"
                value={companySettings.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="(555) 123-4567"
                data-testid="company-phone-input"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={companySettings.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="contact@company.com"
                data-testid="company-email-input"
              />
            </div>
            <div>
              <Label htmlFor="website">Website</Label>
              <Input
                id="website"
                type="url"
                value={companySettings.website}
                onChange={(e) => handleChange('website', e.target.value)}
                placeholder="www.company.com"
                data-testid="company-website-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Address */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Business Address</CardTitle>
            <CardDescription>Your company's physical location</CardDescription>
          </CardHeader>
          <CardContent>
            <div>
              <Label htmlFor="address">Full Address</Label>
              <Input
                id="address"
                value={companySettings.address}
                onChange={(e) => handleChange('address', e.target.value)}
                placeholder="123 Main Street, Suite 100, City, State, ZIP"
                data-testid="company-address-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Representative */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Default Representative</CardTitle>
            <CardDescription>Person who signs proposals on behalf of the company</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="representative_name">Representative Name</Label>
                <Input
                  id="representative_name"
                  value={companySettings.representative_name}
                  onChange={(e) => handleChange('representative_name', e.target.value)}
                  placeholder="John Smith"
                  data-testid="rep-name-input"
                />
              </div>
              <div>
                <Label htmlFor="representative_title">Title</Label>
                <Input
                  id="representative_title"
                  value={companySettings.representative_title}
                  onChange={(e) => handleChange('representative_title', e.target.value)}
                  placeholder="Operations Manager"
                  data-testid="rep-title-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Separator />

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={loading} size="lg" data-testid="save-company-settings-button">
          <Save className="h-4 w-4 mr-2" />
          {loading ? 'Saving...' : 'Save Company Settings'}
        </Button>
      </div>
    </div>
  );
}
